/**
 * Setup Account Page
 * Allows users to complete account setup after invitation
 */

import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { supabase, signOutCompletely } from "../services/supabase";
import Button from "../components/common/Button";
import Input from "../components/common/Input";
import Card from "../components/ui/Card";

const SetupAccount: React.FC = () => {
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isValidToken, setIsValidToken] = useState(true);
  const [userEmail, setUserEmail] = useState("");
  const [userRole, setUserRole] = useState("");
  const [isInitializing, setIsInitializing] = useState(true);

  // The recovery-link flow fires several auth events (INITIAL_SESSION,
  // PASSWORD_RECOVERY, SIGNED_IN) for the SAME session, and the profile load is
  // deferred out of the auth callback (setTimeout) plus a getSession fallback.
  // Without guards those overlap into duplicate loads, and a deferred load can
  // resolve after the component unmounts (a React "setState on unmounted" leak).
  // mountedRef gates every post-await setState; loadTriggeredRef makes the load
  // fire at most once.
  const mountedRef = useRef(true);
  const loadTriggeredRef = useRef(false);

  useEffect(() => {
    mountedRef.current = true;

    // Function to load profile data once we have a user. Runs AT MOST ONCE, and
    // never touches state after unmount.
    const loadProfile = async (userId: string) => {
      if (loadTriggeredRef.current) {
        return;
      } // dedup: only the first trigger wins
      loadTriggeredRef.current = true;
      try {
        console.log("[SetupAccount] Loading profile for user:", userId);

        // Get user profile
        const { data: profile, error: profileError } = await supabase
          .from("profiles")
          .select("email, role")
          .eq("id", userId)
          .single();

        if (!mountedRef.current) {
          return;
        }

        if (profileError || !profile) {
          console.error("[SetupAccount] Profile error:", profileError);
          setError(
            "Could not load account information. Please contact support.",
          );
          setIsValidToken(false);
          return;
        }

        console.log(
          "[SetupAccount] Profile loaded:",
          profile.email,
          profile.role,
        );
        setUserEmail(profile.email);
        setUserRole(profile.role);
        setIsValidToken(true);
      } catch (err) {
        console.error("[SetupAccount] Profile load error:", err);
        if (mountedRef.current) {
          setError(
            "Failed to load account information. Please try again or request a new invitation.",
          );
          setIsValidToken(false);
        }
      } finally {
        if (mountedRef.current) {
          setIsInitializing(false);
        }
      }
    };

    // Listen for auth state changes - this catches the session established from URL hash
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      console.log(
        "[SetupAccount] Auth state change:",
        event,
        session?.user?.id,
      );

      if (session?.user) {
        // Handle all events that provide a valid session:
        // - INITIAL_SESSION: When Supabase processes URL hash tokens on init
        // - SIGNED_IN: When user signs in
        // - TOKEN_REFRESHED: When session is refreshed
        if (
          event === "INITIAL_SESSION" ||
          event === "SIGNED_IN" ||
          event === "TOKEN_REFRESHED" ||
          event === "PASSWORD_RECOVERY"
        ) {
          console.log("[SetupAccount] Valid session from event:", event);
          const userId = session.user.id;
          // Supabase auth callbacks must return synchronously. A query awaited
          // inside the callback can block recovery-link session processing.
          setTimeout(() => {
            if (mountedRef.current) {
              void loadProfile(userId);
            }
          }, 0);
        }
      } else if (event === "SIGNED_OUT") {
        // User signed out
        console.log("[SetupAccount] User signed out");
      }
    });

    // Also check if there's an existing session (for page refreshes)
    const checkExistingSession = async () => {
      try {
        const {
          data: { session },
          error: sessionError,
        } = await supabase.auth.getSession();

        console.log(
          "[SetupAccount] Existing session check:",
          session?.user?.id,
        );

        if (sessionError) {
          console.error("[SetupAccount] Session error:", sessionError);
        }

        if (session?.user) {
          await loadProfile(session.user.id);
        } else {
          // No session yet - wait for URL hash processing
          // Supabase processes the URL hash asynchronously
          // Try multiple times with increasing delays
          let retryCount = 0;
          const maxRetries = 3;
          const retryDelays = [500, 1000, 2000]; // ms

          const attemptRetry = async () => {
            const {
              data: { session: retrySession },
            } = await supabase.auth.getSession();

            if (retrySession?.user) {
              console.log(
                "[SetupAccount] Session found on retry",
                retryCount + 1,
              );
              await loadProfile(retrySession.user.id);
            } else if (retryCount < maxRetries - 1) {
              retryCount++;
              console.log(
                "[SetupAccount] No session, retry",
                retryCount,
                "of",
                maxRetries,
              );
              setTimeout(() => {
                if (mountedRef.current) {
                  void attemptRetry();
                }
              }, retryDelays[retryCount]);
            } else {
              console.log("[SetupAccount] No session after all retries");
              if (!mountedRef.current) {
                return;
              }
              setError(
                "Invalid or expired setup link. Please request a new invitation.",
              );
              setIsValidToken(false);
              setIsInitializing(false);
            }
          };

          // Start first retry after initial delay
          setTimeout(() => {
            if (mountedRef.current) {
              void attemptRetry();
            }
          }, retryDelays[0]);
        }
      } catch (err) {
        console.error("[SetupAccount] Session check error:", err);
        if (!mountedRef.current) {
          return;
        }
        setError(
          "Failed to verify setup link. Please try again or request a new invitation.",
        );
        setIsValidToken(false);
        setIsInitializing(false);
      }
    };

    checkExistingSession();

    return () => {
      mountedRef.current = false;
      subscription.unsubscribe();
    };
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    // Validation
    if (password.length < 8) {
      setError("Password must be at least 8 characters long");
      return;
    }

    if (password !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }

    try {
      setIsLoading(true);

      // Update the user's password
      const { error: updateError } = await supabase.auth.updateUser({
        password: password,
      });

      if (updateError) {
        throw updateError;
      }

      console.log("[SetupAccount] Password set successfully, signing out");

      // Sign out the user so they can log in with their new password.
      // signOutCompletely(), not supabase.auth.signOut(): a failed POST would leave the
      // setup session live, and /login would then redirect into the app rather than showing
      // the form the user is being sent to.
      await signOutCompletely();

      // Success! Redirect to login with success message
      navigate("/login?setup=success");
    } catch (err: any) {
      console.error("[SetupAccount] Account setup error:", err);
      setError(err.message || "Failed to complete account setup");
    } finally {
      setIsLoading(false);
    }
  };

  const formatRole = (role: string): string => {
    // Convert role to readable format (e.g., 'super_admin' -> 'Super Admin')
    return role
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        {/* Logo */}
        <div className="flex justify-center">
          <img
            src="/logowhite.png"
            alt="U.S. LawShield"
            className="h-16 w-auto"
          />
        </div>
        <h2 className="mt-6 text-center text-3xl font-bold text-gray-900">
          Welcome to USLS Relevate
        </h2>
        <p className="mt-2 text-center text-sm text-gray-600">
          Complete your account setup to get started
        </p>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <Card padding="lg">
          {isInitializing ? (
            <div className="flex flex-col items-center justify-center py-8 space-y-4">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-600"></div>
              <p className="text-sm text-gray-600">Loading your account...</p>
            </div>
          ) : !isValidToken ? (
            <div className="space-y-4">
              <div className="p-4 rounded-lg bg-red-50 border border-red-200">
                <p className="text-sm text-red-800">{error}</p>
              </div>
              <Button onClick={() => navigate("/login")} className="w-full">
                Go to Login
              </Button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Account Info */}
              <div className="space-y-4 p-4 rounded-lg bg-blue-50 border border-blue-200">
                <div>
                  <p className="text-sm font-medium text-gray-700">Email</p>
                  <p className="text-sm text-gray-900">{userEmail}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-700">Role</p>
                  <p className="text-sm text-gray-900">
                    {formatRole(userRole)}
                  </p>
                </div>
              </div>

              {/* Password Fields */}
              <div>
                <Input
                  id="password"
                  name="password"
                  type="password"
                  label="Password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter password (min 8 characters)"
                  required
                />
              </div>

              <div>
                <Input
                  id="confirmPassword"
                  name="confirmPassword"
                  type="password"
                  label="Confirm Password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Confirm your password"
                  required
                />
              </div>

              {error && (
                <div className="p-4 rounded-lg bg-red-50 border border-red-200">
                  <p className="text-sm text-red-800">{error}</p>
                </div>
              )}

              <Button type="submit" disabled={isLoading} className="w-full">
                {isLoading ? "Setting up..." : "Complete Setup"}
              </Button>
            </form>
          )}
        </Card>
      </div>
    </div>
  );
};

export default SetupAccount;
